import { useState } from "react";
import { ALLOWED_FILE_TYPES, MAX_FILES, contentTypeFor, uploadRequestSchema, type FileRef } from "@server/uploads/files";
import { ApiError, uploadFile } from "../api/client";
import { Field } from "./ui";

export interface Attached {
  id: string;
  name: string;
  size: number;
  status: "uploading" | "ready" | "error";
  error?: string;
  ref?: FileRef;
}

const ACCEPT = Object.values(ALLOWED_FILE_TYPES)
  .flat()
  .map((extension) => `.${extension}`)
  .join(",");

const sizeLabel = (bytes: number): string => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

// What the form still has to wait for or fix before it can send, or undefined when the files are ready.
export function unfinishedFiles(attached: readonly Attached[]): string | undefined {
  if (attached.some((item) => item.status === "uploading")) {
    return "Wait for the files to finish uploading.";
  }
  return attached.some((item) => item.status === "error") ? "Remove the files that could not be uploaded." : undefined;
}

export const fileRefs = (attached: readonly Attached[]): FileRef[] => attached.flatMap((item) => (item.status === "ready" && item.ref ? [item.ref] : []));

// Each file is uploaded as soon as it is chosen, so submitting only has to send the references.
export function FilePicker({
  attached,
  onChange,
  error,
  hint,
}: {
  attached: Attached[];
  onChange: (update: (current: Attached[]) => Attached[]) => void;
  error?: string;
  hint: string;
}) {
  const [limitError, setLimitError] = useState<string>();
  const update = (id: string, change: Partial<Attached>): void => onChange((current) => current.map((item) => (item.id === id ? { ...item, ...change } : item)));

  const addFiles = (list: FileList | null): void => {
    const chosen = Array.from(list ?? []);
    setLimitError(chosen.length + attached.length > MAX_FILES ? `You can attach up to ${MAX_FILES} files.` : undefined);
    for (const file of chosen.slice(0, Math.max(0, MAX_FILES - attached.length))) {
      const id = crypto.randomUUID();
      const check = uploadRequestSchema.safeParse({ name: file.name, contentType: contentTypeFor(file), size: file.size });
      if (!check.success) {
        onChange((current) => [...current, { id, name: file.name, size: file.size, status: "error", error: check.error.issues[0]?.message ?? "This file can't be attached" }]);
        continue;
      }
      onChange((current) => [...current, { id, name: file.name, size: file.size, status: "uploading" }]);
      uploadFile(file).then(
        (ref) => update(id, { status: "ready", ref }),
        (failure: unknown) => update(id, { status: "error", error: failure instanceof ApiError ? failure.message : "The upload failed. Try again." }),
      );
    }
  };

  return (
    <Field id="files" label="Files & screenshots" optional error={error ?? limitError} hint={hint}>
      {/* The browser's own control would keep saying "No file chosen" because it is reset after each pick, so it is hidden and replaced by a button and a count. */}
      <div className="file-pick">
        <label htmlFor="files" className={`btn btn-secondary btn-sm${attached.length >= MAX_FILES ? " is-disabled" : ""}`}>
          Choose files
        </label>
        <span className="hint">{attached.length === 0 ? "No files chosen" : `${attached.length} of ${MAX_FILES} attached`}</span>
        <input
          id="files"
          className="file-input"
          type="file"
          multiple
          accept={ACCEPT}
          disabled={attached.length >= MAX_FILES}
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>
      {attached.length > 0 && (
        <ul className="file-chips">
          {attached.map((item) => (
            <li key={item.id} className={`file-chip file-chip-${item.status}`}>
              <span>
                📎 {item.name} · {sizeLabel(item.size)}
                {item.status === "uploading" && " · uploading…"}
                {item.status === "error" && ` · ${item.error}`}
              </span>
              <button type="button" aria-label={`Remove ${item.name}`} onClick={() => onChange((current) => current.filter((other) => other.id !== item.id))}>
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </Field>
  );
}
